import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import can from 'ember-can/helpers/can';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import EmberPopover from 'ember-tooltips/components/ember-popover';
import { and, or } from 'ember-truth-helpers';
import type BlacklistFilter from 'garaje/models/blacklist-filter';
import type BlacklistVisitorProfileModel from 'garaje/models/blacklist-visitor-profile';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type MetricsService from 'garaje/services/metrics';

const FLAG = 'vis-blocklist-linked-profile';

interface BlocklistLinkedProfileBadgeSignature {
  Args: {
    /** The blocklist row whose `visitorProfile` relationship (if any) this badge links to. */
    filter: BlacklistFilter;
    /** Where EmberPopover portals its content; defaults to `.ui__page` so it escapes the table's overflow clip. */
    popperContainer?: string;
    /**
     * The blocklist's parent relationship ("location" | "property"). The badge's Back target
     * (`from=blocklist` → location Security) is only correct for the location blocklist, so the
     * badge is suppressed outside it. Defaults to "location". Property support (its own Back
     * target) is future scope.
     */
    context?: string;
  };
  Element: HTMLDivElement;
}

/**
 * A small badge in the blocklist table's name cell, shown only when the filter is linked to a
 * Visitor Directory profile. The badge is itself a keyboard-accessible link to that profile
 * (mirroring the trigger-link pattern of the shipped VisitorProfileHoverCard), and hovering it
 * reveals a card with a supplementary "View profile →" link. Reuses EmberPopover (not EmberTooltip)
 * because the card contains an interactive link, and `@popperContainer` so it survives the table's
 * overflow/z-index clipping.
 */
export default class BlocklistLinkedProfileBadge extends Component<BlocklistLinkedProfileBadgeSignature> {
  @service declare featureFlags: FeatureFlagsService;
  @service declare metrics: MetricsService;

  // belongsTo().value() is untracked, so this relies on the service sideloading `visitor-profile`
  // in the same query — present at first render; it won't recompute if the relationship resolves
  // async later. Null until the BE exposes the relationship — fails closed.
  get visitorProfile(): BlacklistVisitorProfileModel | null {
    return (this.args.filter?.belongsTo?.('visitorProfile')?.value?.() ?? null) as BlacklistVisitorProfileModel | null;
  }

  get visitorProfileId(): string | undefined {
    return this.visitorProfile?.id ?? undefined;
  }

  get isFeatureEnabled(): boolean {
    return this.featureFlags.isEnabled(FLAG);
  }

  // Only the location blocklist routes `from=blocklist` back correctly; suppress
  // the badge in the property blocklist (which shares this table) so a property
  // admin's Back doesn't land on the location Security page.
  get isLocationBlocklist(): boolean {
    return (this.args.context ?? 'location') === 'location';
  }

  @action
  trackViewProfileClick(): void {
    this.metrics.trackEvent('Visitors Profiles View Profile Link Click', {
      visitor_profile_id: this.visitorProfileId,
      from: 'blocklist',
      id: this.args.filter?.id,
    });
  }

  <template>
    {{#if (and this.isFeatureEnabled this.isLocationBlocklist this.visitorProfileId (can "read visitor-profile"))}}
      {{! Wrapper is the EmberPopover anchor (it uses parentNode); inline-flex + w-fit keeps it tight. }}
      <div class="inline-flex w-fit" ...attributes>
        <LinkTo
          @route="visitors.profiles.show"
          @model={{this.visitorProfileId}}
          @query={{hash from="blocklist" id=@filter.id}}
          {{on "click" this.trackViewProfileClick}}
          class="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gem-60 text-white flex-shrink-0 ring-2 ring-white no-underline hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gem-50"
          aria-label="Linked to a Visitor Directory profile. View profile."
          data-test-blocklist-linked-profile-badge
        >
          {{svgJar "link-16" width="12" height="12"}}
        </LinkTo>
        {{! `top-start`, not `top`: the card is many times wider than the 20px badge, so centering
            it on the anchor overhangs to the left — and this badge sits in the table's first
            column, where that overhang runs off the table edge. Aligning the card's left edge to
            the badge lets it run rightward across the row instead. }}
        <EmberPopover
          @popperContainer={{or @popperContainer ".ui__page"}}
          @side="top-start"
          @spacing={{2}}
          @tooltipClass="z-50 rounded-[7px] shadow-[0_3.646px_7.292px_0_rgba(0,0,0,0.12)] bg-white border border-carbon-20 p-3 [&_.ember-popover-arrow]:hidden"
          data-test-blocklist-linked-profile-hover-card
        >
          {{! Single row: gem link badge + text + inline "View profile" link, all one line —
              whitespace-nowrap so it never wraps. }}
          <div class="flex items-start gap-1.5 whitespace-nowrap">
            <span
              class="inline-flex h-[17px] w-[17px] flex-shrink-0 items-center justify-center rounded-full bg-gem-60 text-white"
              aria-hidden="true"
            >
              {{svgJar "link-16" width="11" height="11"}}
            </span>
            <p class="text-carbon-80 text-xs leading-[1.5] m-0">
              Linked to a Visitor Directory profile.
              <LinkTo
                @route="visitors.profiles.show"
                @model={{this.visitorProfileId}}
                @query={{hash from="blocklist" id=@filter.id}}
                {{on "click" this.trackViewProfileClick}}
                class="text-gem-60 no-underline hover:text-gem-60 hover:no-underline"
                data-test-blocklist-view-profile-link
              >
                View profile
                <span aria-hidden="true">→</span>
              </LinkTo>
            </p>
          </div>
        </EmberPopover>
      </div>
    {{/if}}
  </template>
}
